<script setup lang="ts">
import DashboardContentLayout from '@/components/layouts/dashboardLayout/content/DashboardContentLayout.vue';
import MainViewCard from '@/components/common/MainViewCard.vue';
import SlaughtersWeeklyChart from '@/components/charts/SlaughtersWeeklyChart.vue';
import RecordsTable from '@/components/records/RecordsTable.vue';
import BulkCreateButton from '@/components/records/BulkCreateButton.vue';
import { Button } from '@/components/ui/button';
import { ColumnVisibilityMenu } from '@/components/ui/data-table';
import colors from '@/plugins/tailwind';
import { BULK_CREATE, useSlaughtersPage } from './useSlaughtersPage';

/*
 * Same shape as pigsInsertions/pigsSold: this page holds its own data rather than delegating it to
 * a single child. The chart and the table read the same fetch - the chart bins every loaded row and
 * stays that way, and the table shows the subset the picked week and the header facets leave. The
 * column-visibility control rides in `RecordsTable`'s own `#actions` slot, beside its heading -
 * `RecordsTable` renders it without knowing what it is.
 */
const {
    bins,
    columnFilters,
    columns,
    columnToggleOptions,
    facetOptions,
    isError,
    isFetching,
    refetch,
    refreshAfterFormActivity,
    resetSelection,
    selectWeek,
    selectedWeekStart,
    setAllColumns,
    sorting,
    subtitle,
    toggleColumn,
    totalsSubheader,
    visibleRows,
} = useSlaughtersPage();
</script>

<template>
    <DashboardContentLayout class="space-y-8" :heading="$t('views.pigs.slaughters.header.heading')" :has-period-picker="true" :has-pig-type-picker="true">
        <div v-if="isError" class="py-14 text-center px-4 text-gray-500 text-lg space-y-4">
            <div>{{ $t('views.pigs.records.errors.couldNotLoadData') }}</div>
            <Button variant="outline" @click="refetch()">{{ $t('views.pigs.records.errors.retry') }}</Button>
        </div>
        <div v-else class="space-y-8">
            <MainViewCard class="py-4 px-2">
                <SlaughtersWeeklyChart
                    :bins="bins"
                    :selected-week-start="selectedWeekStart"
                    :pigs-color="colors.chart.slaughters"
                    :discarded-color="colors.chart.slaughtersDiscarded"
                    :title="$t('views.pigs.slaughters.chart.heading')"
                    @week-selected="selectWeek"
                    @selection-reset="resetSelection"
                />
            </MainViewCard>
            <MainViewCard class="overflow-hidden">
                <RecordsTable
                    v-model:sorting="sorting"
                    v-model:column-filters="columnFilters"
                    :columns="columns"
                    :rows="visibleRows"
                    :facet-options="facetOptions"
                    :loading="isFetching"
                    :heading="$t('views.pigs.slaughters.table.heading')"
                    :subtitle="subtitle"
                    :totals-subheader="totalsSubheader"
                    @form-activity="refreshAfterFormActivity"
                >
                    <template #actions>
                        <div class="flex items-center gap-2">
                            <BulkCreateButton :form-id="BULK_CREATE.formId" :label="$t(BULK_CREATE.labelKey)" @form-activity="refreshAfterFormActivity" />
                            <ColumnVisibilityMenu
                                :options="columnToggleOptions"
                                :label="$t('views.pigs.slaughters.table.columnsButton')"
                                @toggle="toggleColumn"
                                @select-all="setAllColumns(true)"
                                @deselect-all="setAllColumns(false)"
                            />
                        </div>
                    </template>
                </RecordsTable>
            </MainViewCard>
        </div>
    </DashboardContentLayout>
</template>
